"use client"

import {
  Toaster as Sonner,
  toast as sonnerToast,
  type ExternalToast,
  type ToastT,
} from "sonner"
import { Icon } from "./icon/Icon"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Minimum time a snackbar stays visible. Designs occasionally specify shorter
 * durations (e.g. 3000ms); we override those so users always have enough time
 * to read confirmations.
 */
const MIN_DURATION_MS = 5000

/**
 * Snackbars must never stack. If a second one is triggered while another is
 * still visible, it is queued and shown only once the previous one is closed
 * (either automatically after its duration or manually via the close button /
 * action).
 *
 * The patch below wraps every sonner `toast.*` method so that all callers —
 * including files that `import { toast } from "sonner"` directly — go through
 * the same queue without having to change individual callsites.
 */
const PATCH_FLAG = Symbol.for("flextribe.sonner.snackbarQueuePatched")

type QueueableKind =
  | "success"
  | "info"
  | "warning"
  | "error"
  | "message"
  | "loading"
  | "custom"

type QueuedToast = {
  kind: QueueableKind
  args: unknown[]
  id: string | number
}

type QueueState = {
  queue: QueuedToast[]
  activeId: string | number | null
  counter: number
}

const normalizeDuration = (options?: ExternalToast): ExternalToast => {
  const next: ExternalToast = options ? { ...options } : {}
  if (next.duration !== Infinity) {
    const provided =
      typeof next.duration === "number" ? next.duration : MIN_DURATION_MS
    next.duration = Math.max(provided, MIN_DURATION_MS)
  }
  return next
}

if (
  typeof window !== "undefined" &&
  !(sonnerToast as unknown as Record<symbol, unknown>)[PATCH_FLAG]
) {
  ;(sonnerToast as unknown as Record<symbol, unknown>)[PATCH_FLAG] = true

  const state: QueueState = { queue: [], activeId: null, counter: 0 }

  const genId = () => `flextribe-snackbar-${Date.now()}-${state.counter++}`

  const originals = {
    success: sonnerToast.success.bind(sonnerToast),
    info: sonnerToast.info.bind(sonnerToast),
    warning: sonnerToast.warning.bind(sonnerToast),
    error: sonnerToast.error.bind(sonnerToast),
    message: sonnerToast.message.bind(sonnerToast),
    loading: sonnerToast.loading.bind(sonnerToast),
    custom: sonnerToast.custom.bind(sonnerToast),
    dismiss: sonnerToast.dismiss.bind(sonnerToast),
  }

  const finish = (id: string | number) => {
    const queuedIndex = state.queue.findIndex((entry) => entry.id === id)
    if (queuedIndex !== -1) state.queue.splice(queuedIndex, 1)
    if (state.activeId === id) {
      state.activeId = null
      drain()
    }
  }

  const showNow = (entry: QueuedToast) => {
    const fn = originals[entry.kind] as (...args: unknown[]) => string | number
    fn(...entry.args)
    state.activeId = entry.id
  }

  const drain = () => {
    if (state.activeId !== null) return
    const next = state.queue.shift()
    if (next) showNow(next)
  }

  const wrapCallbacks = (options: ExternalToast): ExternalToast => {
    const userOnDismiss = options.onDismiss
    const userOnAutoClose = options.onAutoClose
    return {
      ...options,
      onDismiss: (toast: ToastT) => {
        userOnDismiss?.(toast)
        finish(toast.id)
      },
      onAutoClose: (toast: ToastT) => {
        userOnAutoClose?.(toast)
        finish(toast.id)
      },
    }
  }

  const wrap = <K extends QueueableKind>(kind: K) => {
    const original = originals[kind] as (
      message: unknown,
      data?: ExternalToast,
    ) => string | number

    return (message: unknown, options?: ExternalToast) => {
      // Updates to an existing toast (same id) must not be queued — they are
      // in-place replacements handled by sonner directly.
      if (options?.id !== undefined) {
        return original(message, wrapCallbacks(normalizeDuration(options)))
      }

      const id = genId()
      const patched = wrapCallbacks({
        ...normalizeDuration(options),
        id,
      })
      const entry: QueuedToast = { kind, args: [message, patched], id }

      if (state.activeId === null) {
        showNow(entry)
      } else {
        state.queue.push(entry)
      }
      return id
    }
  }

  const queueableKinds: QueueableKind[] = [
    "success",
    "info",
    "warning",
    "error",
    "message",
    "loading",
    "custom",
  ]

  for (const kind of queueableKinds) {
    Object.defineProperty(sonnerToast, kind, {
      value: wrap(kind),
      writable: true,
      configurable: true,
    })
  }

  Object.defineProperty(sonnerToast, "dismiss", {
    value: (id?: string | number) => {
      if (id === undefined) {
        state.queue.length = 0
        state.activeId = null
      } else {
        const idx = state.queue.findIndex((entry) => entry.id === id)
        if (idx !== -1) state.queue.splice(idx, 1)
        if (state.activeId === id) state.activeId = null
      }
      const result = originals.dismiss(id)
      // Sonner's onDismiss usually fires after `dismiss()` and will drain the
      // queue. Schedule a drain anyway in case the toast wasn't yet visible
      // (e.g. dismissed before it got its turn).
      queueMicrotask(drain)
      return result
    },
    writable: true,
    configurable: true,
  })
}

const Snackbar = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      duration={MIN_DURATION_MS}
      className="toaster group sm:min-w-[616px] sm:max-w-[827px]"
      position="bottom-center"
      closeButton
      visibleToasts={1}
      icons={{
        error: <></>,
        info: <></>,
        success: <></>,
        warning: <></>,
        loading: (
          <div className="text-on-primary aspect-square size-5 animate-spin">
            <Icon icon="progress_activity" size="1.25rem" weight={400} />
          </div>
        ),
      }}
      toastOptions={{
        unstyled: true,
        style: {
          height: "auto",
        },
        className:
          "bg-inverse-surface text-inverse-on-surface grid grid-cols-[min-content_auto_min-content_min-content_min-content] rounded-md min-h-12 pl-4 pr-2 py-1 h-fit w-full",
        classNames: {
          toast:
            "group/toast py-1 flex items-center py-1 group-[.toaster]:pointer-events-auto",
          content: "text-md font-semibold col-start-2 mr-4",
          icon: "col-start-1 mr-2 hidden group-data-[type=loading]/toast:block",
          cancelButton:
            "cursor-pointer row-start-1 col-start-4 w-auto hover:bg-[#FFFFFF]/10 text-sm py-1 !ml-2 my-2 h-10 px-3 text-inverse-primary rounded-full aspect-square focus:border focus:border-inverse-primary focus:outline-none text-nowrap",
          actionButton:
            "cursor-pointer row-start-1 col-start-3 w-auto hover:bg-[#FFFFFF]/10 text-sm py-1 h-10 px-3 text-inverse-primary rounded-full focus:border focus:border-inverse-primary focus:outline-none text-nowrap",
          closeButton:
            "cursor-pointer size-10 *:scale-150 transform-none col-start-5 row-start-1 rounded-full !bg-inverse-surface border-none text-inverse-on-surface hover:!bg-[#FFFFFF]/10 static right-0 left-0",
        },
      }}
      {...props}
    />
  )
}

export { Snackbar }
